import { useEffect, useMemo, useState } from "react";

import { isOpenFeedDrop } from "../services/dropsFeedService";
import { normalizeCreator } from "./homeSeedPreview";

function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

function creatorIdSet(creator) {
  return new Set(
    [creator.id, creator.uid, creator.featuredId, creator.creatorId].filter(Boolean)
  );
}

function pickHeroDrop(creator, drops = []) {
  const ids = creatorIdSet(creator);
  const openDrops = drops.filter((d) => isOpenFeedDrop(d) && ids.has(d.creatorId));

  if (openDrops.length) {
    return openDrops.sort((a, b) => viewsLeft(a) - viewsLeft(b))[0];
  }

  return {
    id: `featured-${creator.id}`,
    title: creator.displayName || "Featured creator",
    subtitle: creator.bio?.split(".")[0] || "Featured today on 100ViewsOnly",
    thumbnailUrl:
      creator.cardImage ||
      creator.thumbnailUrl ||
      creator.bannerImage ||
      creator.bannerUrl ||
      creator.avatarUrl ||
      creator.avatar ||
      "",
    niche: creator.niche,
    viewCount: 0,
    maxViews: 100,
    accessType: "free",
    status: "open",
    creatorId: creator.id,
    _synthetic: true,
  };
}

/**
 * Build Gallery hero slides from today's platform/featured creators (10 total).
 */
export function buildGalleryFeaturedSlides(featuredCreators, drops = []) {
  return (featuredCreators || [])
    .map((creator) => ({
      creator,
      drop: pickHeroDrop(creator, drops),
    }))
    .filter((slide) => slide.creator && slide.drop);
}

/**
 * Build hero slides from open drops + creator lookup.
 * One slide per open drop (creators with open drops appear once per drop).
 */
export function buildFeaturedSlides(openDrops, creatorById, { followingIds = null } = {}) {
  const followingSet = followingIds ? new Set(followingIds) : null;

  return openDrops
    .filter(isOpenFeedDrop)
    .filter((d) => !followingSet || followingSet.has(d.creatorId))
    .map((drop) => {
      const raw = creatorById.get(drop.creatorId);
      const creator = raw
        ? normalizeCreator(drop.creatorId, raw)
        : normalizeCreator(drop.creatorId, {
            displayName: drop.creatorDisplayName || "Creator",
            handle: drop.creatorUsername || drop.creatorHandle,
            avatarUrl: drop.creatorAvatar || "",
          });
      return { creator, drop };
    })
    .filter((s) => s.creator && s.drop);
}

/** Stable daily index — same key always picks the same slot. */
function deterministicIndex(key, length) {
  if (length <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return hash % length;
}

/**
 * Pick one open drop from followed creators for the Home featured card.
 * Personalized + stable for the UTC day (no revolving carousel).
 * `feedRows` should already be filtered to the current user's following.
 */
export function pickHomeFeaturedFromFeed(feedRows, userId, dateStr) {
  const open = (feedRows || []).filter((r) => r?.drop && r?.creator && isOpenFeedDrop(r.drop));
  if (!open.length) return null;

  const day = dateStr || new Date().toISOString().slice(0, 10);
  const key = `${day}:${userId || "anon"}`;
  const index = deterministicIndex(key, open.length);
  return open[index];
}

export function useRevolvingFeatured(slides, intervalMs = 8000) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const safeSlides = useMemo(() => slides || [], [slides]);

  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [safeSlides.length]);

  useEffect(() => {
    if (safeSlides.length <= 1) return undefined;

    const timer = setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((prev) => (prev + 1) % safeSlides.length);
        setVisible(true);
      }, 450);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [safeSlides.length, intervalMs]);

  const slide = safeSlides[index] || null;

  return {
    slide,
    index,
    total: safeSlides.length,
    visible,
    slides: safeSlides,
  };
}
